import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box, TextField, Button, Typography,
  CircularProgress, Alert, InputAdornment, IconButton,
} from "@mui/material";
import LockOutlinedIcon          from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon    from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

function CreatePassword() {
  const [password,        setPassword]        = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPass,        setShowPass]        = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [success,         setSuccess]         = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const user = (() => {
      try { return JSON.parse(localStorage.getItem("user")); }
      catch { return null; }
    })();
    if (!user) { navigate("/login"); return; }
    if (user.password !== null && user.password !== undefined) navigate("/");
  }, [navigate]);

  const handleSubmit = async () => {
    setError(null);
    if (!password || !passwordConfirm) { setError("Semua field wajib diisi."); return; }
    if (password.length < 8) { setError("Password minimal 8 karakter."); return; }
    if (password !== passwordConfirm) { setError("Konfirmasi password tidak cocok."); return; }

    try {
      setLoading(true);
      await api.post("/user/set-password", {
        password,
        password_confirmation: passwordConfirm,
      });
      const user = JSON.parse(localStorage.getItem("user"));
      user.password = "set";
      localStorage.setItem("user", JSON.stringify(user));
      setSuccess(true);
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      setError(err.response?.data?.message ?? "Terjadi kesalahan.");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px", fontSize: "0.8rem",
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: "#111827", borderWidth: "1.5px" },
    },
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex",
      alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{
        position: "absolute", inset: 0,
        backgroundImage: "url('https://images.unsplash.com/photo-1562774053-701939374585?w=1600&q=80')",
        backgroundSize: "cover", backgroundPosition: "center",
        filter: "blur(6px) brightness(0.45)", transform: "scale(1.05)",
      }} />
      <Box sx={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(17,24,39,0.55) 100%)",
      }} />

      {/* Card — diperkecil */}
      <Box sx={{
        position: "relative", zIndex: 1, width: "100%", maxWidth: 340,
        backgroundColor: "rgba(255,255,255,0.97)", borderRadius: "16px",
        p: { xs: 2.5, sm: 3 },
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)", mx: 2,
      }}>

        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 3 }}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA"
            sx={{ height: 28, width: "auto", objectFit: "contain" }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: "#111827", lineHeight: 1.2 }}>
              BAS-LPKIA
            </Typography>
            <Typography sx={{ fontSize: "0.58rem", color: "#6b7280", lineHeight: 1.2 }}>
              Sistem Pengadaan
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", mb: 0.3 }}>
          Buat Password
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mb: 2.5 }}>
          Buat password untuk login manual selain lewat Google
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, borderRadius: "7px", fontSize: "0.75rem", py: 0.5 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: "7px", fontSize: "0.75rem", py: 0.5 }}>
            Password berhasil dibuat! Mengalihkan...
          </Alert>
        )}

        {/* Password */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
            Password Baru
          </Typography>
          <TextField fullWidth size="small"
            type={showPass ? "text" : "password"}
            placeholder="Minimal 8 karakter"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPass(!showPass)} edge="end">
                    {showPass
                      ? <VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                      : <VisibilityOutlinedIcon    sx={{ fontSize: 15, color: "#9ca3af" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx} />
        </Box>

        {/* Konfirmasi */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
            Konfirmasi Password
          </Typography>
          <TextField fullWidth size="small"
            type={showConfirm ? "text" : "password"}
            placeholder="Ulangi password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                    {showConfirm
                      ? <VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                      : <VisibilityOutlinedIcon    sx={{ fontSize: 15, color: "#9ca3af" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx} />
        </Box>

        <Button type="button" fullWidth variant="contained"
          onClick={handleSubmit} disabled={loading || success}
          sx={{
            borderRadius: "7px", textTransform: "none", fontSize: "0.82rem", fontWeight: 700,
            py: 1.1, backgroundColor: "#111827", boxShadow: "none",
            "&:hover": { backgroundColor: "#1f2937", boxShadow: "0 4px 14px rgba(0,0,0,0.15)" },
            "&:disabled": { backgroundColor: "#e5e7eb" },
            transition: "all 0.2s",
          }}>
          {loading ? <CircularProgress size={16} sx={{ color: "#9ca3af" }} /> : "Simpan Password"}
        </Button>

        <Typography onClick={() => navigate("/")} sx={{
          fontSize: "0.72rem", color: "#9ca3af", textAlign: "center", mt: 2,
          cursor: "pointer", "&:hover": { color: "#111827" }, transition: "color 0.15s",
        }}>
          Lewati, saya tetap pakai Google
        </Typography>
      </Box>
    </Box>
  );
}

export default CreatePassword;